import React from 'react'
// c/xampp/htdocs/miners-market-main/components/PriceRequestsManager.js
import Pagination from './Pagination.jsx';

export default function PriceRequestsManager() {
    const [requests, setRequests] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [actionMessage, setActionMessage] = React.useState({ type: '', text: '' });
    const [expandedProductId, setExpandedProductId] = React.useState(null);
    const [isProcessing, setIsProcessing] = React.useState(null); // Tracks the ID of the request being processed

    // --- NEW: Pagination State ---
    const [currentPage, setCurrentPage] = React.useState(1);
    const itemsPerPage = 15;

    // Helper to check if a date is within the last 24 hours
    const isRecent = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        return (now - date) < (24 * 60 * 60 * 1000);
    };

    const fetchRequests = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/api/admin_get_price_requests.php', { credentials: 'include' });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to fetch price requests.');
            }
            const data = await response.json();
            
            // Sort data: Pending requests first, then by most recent request date
            data.sort((a, b) => {
                const hasPendingA = a.requesters.some(r => r.status !== 'approved');
                const hasPendingB = b.requesters.some(r => r.status !== 'approved');

                if (hasPendingA && !hasPendingB) return -1;
                if (!hasPendingA && hasPendingB) return 1;

                const getLatestDate = (product) => {
                    if (!product.requesters || product.requesters.length === 0) return 0;
                    return Math.max(...product.requesters.map(r => new Date(r.request_date).getTime()));
                };
                return getLatestDate(b) - getLatestDate(a);
            });
            setRequests(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleApprove = async (requestId) => {
        setIsProcessing(requestId);
        setActionMessage({ type: '', text: '' });
        try {
            const response = await fetch('/api/admin_approve_price_request.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ request_id: requestId })
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.message || 'Failed to approve request.');
            }
            setActionMessage({ type: 'success', text: result.message });
            // Refresh the list to show the updated status
            fetchRequests();
        } catch (err) {
            setActionMessage({ type: 'error', text: err.message });
        } finally {
            setIsProcessing(null);
        }
    };

    const toggleExpand = (productId) => {
        setExpandedProductId(prevId => (prevId === productId ? null : productId));
    };

    // --- NEW: Pagination Logic ---
    const totalPages = Math.ceil(requests.length / itemsPerPage);
    const currentRequests = requests.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Price Requests Management</h2>

            {actionMessage.text && (
                <div className={`p-3 rounded-lg text-sm mb-4 ${actionMessage.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {actionMessage.text}
                </div>
            )}

            {loading && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
            {error && <div className="text-red-500 text-center py-4">Error: {error}</div>}
            {!loading && !error && requests.length === 0 && <p className="text-gray-500 text-center">No active price requests.</p>}

            {!loading && !error && requests.length > 0 && (
                <div className="space-y-4">
                    {currentRequests.map(product => (
                        <div key={`${product.listing_type}-${product.product_id}`} className="border rounded-lg">
                            <button onClick={() => toggleExpand(`${product.listing_type}-${product.product_id}`)} className="w-full flex justify-between items-center p-4 text-left hover:bg-gray-50">
                                <div>
                                    <h4 className="font-semibold text-gray-900">{product.product_name}</h4>
                                    <p className="text-gray-600 text-sm">by {product.seller_name} <span className="text-gray-400 font-mono text-xs">({product.listing_number})</span></p>
                                </div>
                                <div className="flex items-center space-x-4">
                                    {product.requesters.some(r => isRecent(r.request_date)) && (
                                        <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">New</span>
                                    )}
                                    {product.requesters.some(r => r.status !== 'approved') && (
                                        <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">Pending</span>
                                    )}
                                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                        {product.requesters.length} {product.requesters.length === 1 ? 'Request' : 'Requests'}
                                    </span>
                                    <i className={`fas fa-chevron-down transition-transform ${expandedProductId === `${product.listing_type}-${product.product_id}` ? 'rotate-180' : ''}`}></i>
                                </div>
                            </button>

                            {expandedProductId === `${product.listing_type}-${product.product_id}` && (
                                <div className="p-4 border-t bg-gray-50 space-y-3">
                                    {product.requesters.map(requester => (
                                        <div key={requester.request_id} className="flex flex-col sm:flex-row justify-between sm:items-center p-3 bg-white border rounded-md">
                                            <div>
                                                <p className="font-medium text-gray-800">
                                                    {requester.buyer_name}
                                                    {isRecent(requester.request_date) && <span className="ml-2 text-green-600 text-xs font-bold">(New)</span>}
                                                </p>
                                                <p className="text-sm text-gray-500">{requester.buyer_email}</p>
                                                <p className="text-xs text-gray-400">Requested on: {new Date(requester.request_date).toLocaleDateString()}</p>
                                            </div>
                                            <div className="mt-2 sm:mt-0">
                                                {requester.status === 'approved' ? (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                                        <i className="fas fa-check-circle mr-2"></i>
                                                        Approved
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleApprove(requester.request_id)}
                                                        disabled={isProcessing === requester.request_id}
                                                        className="bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors text-sm disabled:opacity-50"
                                                    >
                                                        {isProcessing === requester.request_id ? 'Approving...' : 'Allow to See Price'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
    );
}
